/**
 * MembersTab - Onglet "Membres" du rôle.
 *
 * Affiche les profils ayant ce rôle, permet d'en retirer, et propose une
 * modale d'ajout multi-sélection depuis /api/users.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PlusIcon, CloseIcon, UserIcon, CheckIcon, SearchIcon, ShieldIcon } from '../../icons';
import {
  RoleDetail,
  RoleMember,
  API_BASE,
  authHeaders
} from './types';

interface MembersTabProps {
  role: RoleDetail;
  onChanged: () => void;
}

interface UserListEntry {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  external_id: string | null;
  role: string;
  is_active: boolean;
}

function memberDisplayName(m: RoleMember): string {
  if (m.first_name || m.last_name) {
    return `${m.first_name || ''} ${m.last_name || ''}`.trim();
  }
  return m.company_name || m.external_id || m.id;
}

function memberEstabLabel(m: RoleMember): string {
  return m.establishments?.name || m.establishments?.dossier || '—';
}

function memberInitials(m: RoleMember): string {
  const fn = (m.first_name || '').trim();
  const ln = (m.last_name || '').trim();
  if (fn || ln) {
    return `${fn[0] || ''}${ln[0] || ''}`.toUpperCase() || '?';
  }
  if (m.company_name) return m.company_name.slice(0, 2).toUpperCase();
  if (m.external_id) return m.external_id.slice(0, 2).toUpperCase();
  return '?';
}

function MembersTab({ role, onChanged }: MembersTabProps) {
  const [members, setMembers] = useState<RoleMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [estabFilter, setEstabFilter] = useState<string>('all');

  const estabOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) {
      if (m.establishment_id) map.set(m.establishment_id, memberEstabLabel(m));
    }
    return Array.from(map, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [members]);

  const visibleMembers = estabFilter === 'all'
    ? members
    : members.filter(m => m.establishment_id === estabFilter);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`[MembersTab] Fetching /api/roles/${role.id}/members`);
      const res = await fetch(`${API_BASE}/api/roles/${role.id}/members`, {
        headers: authHeaders()
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      setMembers(json.members || []);
      console.log(`[MembersTab] ${(json.members || []).length} membre(s) chargé(s)`);
    } catch (err) {
      console.error('[MembersTab] Erreur fetch:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [role.id]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  async function handleRemove(member: RoleMember) {
    const name = memberDisplayName(member);
    if (!confirm(`Retirer ${name} de ce rôle ?`)) return;

    setActionLoading(member.id);
    setError(null);
    try {
      console.log(`[MembersTab] DELETE /api/roles/${role.id}/members/${member.id}`);
      const res = await fetch(`${API_BASE}/api/roles/${role.id}/members/${member.id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      setMembers(prev => prev.filter(m => m.id !== member.id));
      onChanged();
    } catch (err) {
      console.error('[MembersTab] Erreur remove:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du retrait');
    } finally {
      setActionLoading(null);
    }
  }

  const isGlobalRole = role.name === 'super_admin';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Banniere "role global" : explique pourquoi la liste ignore l'etablissement courant */}
      {isGlobalRole && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          padding: '12px 14px',
          backgroundColor: '#eff6ff',
          color: '#1e40af',
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          fontSize: '13px',
          lineHeight: 1.5
        }}>
          <ShieldIcon size={16} color="#1e40af" style={{ marginTop: '1px', flexShrink: 0 }} />
          <div>
            <strong>Rôle global.</strong> Les super-administrateurs ne sont rattachés à aucun établissement&nbsp;: la liste affiche tous les super-administrateurs de la base, indépendamment de l'établissement actuellement sélectionné dans la TopBar.
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '14px', color: '#475569' }}>
            {estabFilter !== 'all'
              ? <>{visibleMembers.length} / {members.length} utilisateur{members.length > 1 ? 's' : ''}</>
              : <>{members.length} utilisateur{members.length > 1 ? 's' : ''} avec ce rôle</>
            }
            {isGlobalRole && <span style={{ marginLeft: '6px', color: '#94a3b8' }}>(tous établissements)</span>}
            {!isGlobalRole && estabFilter === 'all' && <span style={{ marginLeft: '6px', color: '#94a3b8' }}>(tout le compte)</span>}
          </div>
          {estabOptions.length > 1 && (
            <select
              value={estabFilter}
              onChange={e => setEstabFilter(e.target.value)}
              style={{
                padding: '4px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#374151',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="all">Tous les établissements</option>
              {estabOptions.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          <PlusIcon size={14} color="white" />
          Ajouter des membres
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '10px 12px',
          backgroundColor: '#fef2f2',
          color: '#991b1b',
          border: '1px solid #fca5a5',
          borderRadius: '6px',
          fontSize: '13px'
        }}>
          {error}
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
          Chargement...
        </div>
      ) : members.length === 0 ? (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          color: '#94a3b8',
          fontSize: '14px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px dashed #e2e8f0'
        }}>
          Aucun utilisateur n'a ce rôle pour le moment.
        </div>
      ) : visibleMembers.length === 0 ? (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          color: '#94a3b8',
          fontSize: '14px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px dashed #e2e8f0'
        }}>
          Aucun membre pour cet établissement.
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          {visibleMembers.map(m => {
            const isLoading = actionLoading === m.id;
            return (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  backgroundColor: m.is_active ? 'white' : '#fef3c7'
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: 600,
                  flexShrink: 0
                }}>
                  {memberInitials(m)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#1e293b',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {memberDisplayName(m)}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#94a3b8',
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginTop: '2px'
                  }}>
                    {m.external_id && <span>Code : {m.external_id}</span>}
                    {m.establishment_id && estabFilter === 'all' && estabOptions.length > 1 && (
                      <span>{memberEstabLabel(m)}</span>
                    )}
                    {!m.is_active && (
                      <span style={{
                        color: '#92400e',
                        fontWeight: 500
                      }}>
                        Désactivé
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(m)}
                  disabled={isLoading}
                  title="Retirer de ce rôle"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#fef2f2',
                    color: '#dc2626',
                    border: '1px solid #fca5a5',
                    borderRadius: '6px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.5 : 1,
                    flexShrink: 0
                  }}
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {showAddModal && (
        <AddMembersModal
          roleId={role.id}
          existingMemberIds={new Set(members.map(m => m.id))}
          onClose={() => setShowAddModal(false)}
          onAdded={() => {
            setShowAddModal(false);
            fetchMembers();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

interface AddMembersModalProps {
  roleId: string;
  existingMemberIds: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}

function AddMembersModal({ roleId, existingMemberIds, onClose, onAdded }: AddMembersModalProps) {
  const [users, setUsers] = useState<UserListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[AddMembersModal] Fetching /api/roles/assignable-profiles');
      const res = await fetch(`${API_BASE}/api/roles/assignable-profiles`, {
        headers: authHeaders()
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      const list: UserListEntry[] = (json.profiles || []).map((u: Record<string, unknown>) => ({
        id: String(u.id),
        email: String(u.email || ''),
        first_name: (u.first_name as string) || null,
        last_name: (u.last_name as string) || null,
        company_name: (u.company_name as string) || null,
        external_id: (u.external_id as string) || null,
        role: String(u.role || ''),
        is_active: Boolean(u.is_active)
      }));
      setUsers(list);
    } catch (err) {
      console.error('[AddMembersModal] Erreur fetch profils assignables:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Exclure les users déjà membres du rôle
  const availableUsers = users.filter(u => !existingMemberIds.has(u.id) && u.is_active);

  const filtered = availableUsers.filter(u => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (u.email || '').toLowerCase().includes(q) ||
      `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(q) ||
      (u.company_name || '').toLowerCase().includes(q) ||
      (u.external_id || '').toLowerCase().includes(q)
    );
  });

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSubmit() {
    if (selected.size === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      console.log(`[AddMembersModal] Ajout de ${selected.size} membre(s) au rôle ${roleId}`);
      const ids = Array.from(selected);
      await Promise.all(ids.map(profileId =>
        fetch(`${API_BASE}/api/roles/${roleId}/members`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ profile_id: profileId })
        }).then(async res => {
          const json = await res.json().catch(() => ({}));
          if (!res.ok || !json.success) {
            throw new Error(json?.error?.message || `Erreur ajout (HTTP ${res.status})`);
          }
        })
      ));
      onAdded();
    } catch (err) {
      console.error('[AddMembersModal] Erreur submit:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'ajout');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
            Ajouter des membres
          </div>
          <button
            onClick={onClose}
            style={modalCloseBtn}
            title="Fermer"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ position: 'relative' }}>
            <SearchIcon
              size={14}
              color="#94a3b8"
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
              }}
            />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un utilisateur..."
              autoFocus
              style={{
                width: '100%',
                padding: '8px 10px 8px 32px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {error && (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              fontSize: '13px'
            }}>
              {error}
            </div>
          )}

          <div style={{
            maxHeight: '320px',
            overflowY: 'auto',
            border: '1px solid #e2e8f0',
            borderRadius: '6px'
          }}>
            {loading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                Chargement...
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                {availableUsers.length === 0
                  ? 'Tous les utilisateurs ont déjà ce rôle.'
                  : 'Aucun utilisateur ne correspond à la recherche.'}
              </div>
            ) : (
              filtered.map((u, idx) => {
                const isSelected = selected.has(u.id);
                const fullName = (u.first_name || u.last_name)
                  ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                  : u.company_name || u.external_id || u.email;
                return (
                  <label
                    key={u.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#eff6ff' : 'transparent'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(u.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <UserIcon size={16} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#1e293b',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {fullName}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#94a3b8',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {u.email}
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div style={modalFooter}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={selected.size === 0 || submitting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: selected.size === 0 || submitting ? '#93c5fd' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: selected.size === 0 || submitting ? 'not-allowed' : 'pointer'
            }}
          >
            <CheckIcon size={14} color="white" />
            {submitting ? 'Ajout...' : `Ajouter (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px'
};

const modalBox: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
  maxWidth: '560px',
  width: '100%',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

const modalHeader: React.CSSProperties = {
  padding: '14px 20px',
  borderBottom: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
};

const modalFooter: React.CSSProperties = {
  padding: '12px 20px',
  borderTop: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  backgroundColor: '#f8fafc'
};

const modalCloseBtn: React.CSSProperties = {
  width: '28px',
  height: '28px',
  border: 'none',
  background: 'transparent',
  color: '#64748b',
  cursor: 'pointer',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export default MembersTab;
